import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import {
  ClientDocument,
  DocumentFile,
  GenerateDocumentsRequest,
  GenerateDocumentsResponse,
  UploadScanRequest,
  downloadDocumentRequest,
  generateDocumentsRequest,
  getClientDocumentsRequest,
  getDocumentTemplatesRequest,
  uploadScanRequest,
} from '../api/documentApi'
import { ACTION_FAILED_ALERT_ID } from '../constants/dialogConstants'
import { openDialog } from '../redux/actions/dialogActions'
import { finishLoading, startLoading } from '../redux/actions/loadingActions'

// Stazeni base64 souboru pres data-URI odkaz (vzor InvestmentUpdate.tsx)
export const downloadBase64File = (file: DocumentFile) => {
  const linkSource = `data:${file.mimeType || 'application/octet-stream'};base64,${file.content}`
  const downloadLink = document.createElement('a')
  downloadLink.href = linkSource
  downloadLink.download = file.fileName
  downloadLink.click()
}

export const useDocumentApi = () => {
  const dispatch = useDispatch()

  const getDocumentTemplates = useCallback(async <T,>(): Promise<T | null> => {
    dispatch(startLoading('getDocumentTemplates'))
    try {
      const response = await getDocumentTemplatesRequest<T>()
      return response.data
    } catch (error) {
      console.error(error)
      dispatch(openDialog(ACTION_FAILED_ALERT_ID))
      return null
    } finally {
      dispatch(finishLoading('getDocumentTemplates'))
    }
  }, [])

  const generateDocuments = useCallback(
    async (request: GenerateDocumentsRequest): Promise<GenerateDocumentsResponse | null> => {
      dispatch(startLoading('generateDocuments'))
      try {
        const response = await generateDocumentsRequest(request)
        return response.data
      } catch (error) {
        console.error(error)
        dispatch(openDialog(ACTION_FAILED_ALERT_ID))
        return null
      } finally {
        dispatch(finishLoading('generateDocuments'))
      }
    },
    []
  )

  const getClientDocuments = useCallback(async (clientId: number): Promise<ClientDocument[]> => {
    dispatch(startLoading('getClientDocuments'))
    try {
      const response = await getClientDocumentsRequest(clientId)
      return response.data
    } catch (error) {
      console.error(error)
      return []
    } finally {
      dispatch(finishLoading('getClientDocuments'))
    }
  }, [])

  const downloadDocument = useCallback(async (documentId: number) => {
    dispatch(startLoading('downloadDocument'))
    try {
      const response = await downloadDocumentRequest(documentId)
      downloadBase64File(response.data)
    } catch (error) {
      console.error(error)
      dispatch(openDialog(ACTION_FAILED_ALERT_ID))
    } finally {
      dispatch(finishLoading('downloadDocument'))
    }
  }, [])

  const uploadScan = useCallback(async (request: UploadScanRequest): Promise<ClientDocument | null> => {
    dispatch(startLoading('uploadScan'))
    try {
      const response = await uploadScanRequest(request)
      return response.data
    } catch (error) {
      console.error(error)
      dispatch(openDialog(ACTION_FAILED_ALERT_ID))
      return null
    } finally {
      dispatch(finishLoading('uploadScan'))
    }
  }, [])

  return {
    getDocumentTemplates,
    generateDocuments,
    getClientDocuments,
    downloadDocument,
    uploadScan,
  }
}
